import { trpcVanilla } from "@client/trpc";
import {
  createSyncFeature,
  type StorageLike,
  type SyncFeature,
} from "@client/sync";
import { type ChecklistMarkRow, type RoundVisitKind } from "@lib/todoRailTypes";
import { type ProgressRow } from "@lib/videoStateTypes";

/**
 * Durable outboxes for the To Do rail's writes.
 *
 * They used to be fire-and-forget mutations, which meant a mark made offline
 * showed as done and was simply lost — the UI claimed something the server
 * never received, and nothing ever retried. Watch progress solved this long
 * ago with a queue-and-retry outbox; this is the same machinery, not a second
 * one (`@client/sync`: persisted queue, exponential backoff, online detection,
 * and a commit gate for sends that race an identity flip).
 *
 * Unlike the video-progress outbox these persist to **localStorage on web as
 * well as native**. That is the difference in what the data is worth: a lost
 * 30-second playhead window is re-derived the moment you press play again,
 * while "I read these notes" is a fact about a human that nothing can
 * reconstruct. The key is per-user, so a queue written by one signed-in user
 * can never flush under the next user of a shared device — a stale user's
 * entries just sit inert.
 */

export interface ChecklistMarkPayload {
  roundId: string;
  itemKey: string;
  done: boolean;
  clientUpdatedAt: string;
}

export interface ChecklistMarkFlushResult {
  applied: boolean;
  current: ChecklistMarkRow | null;
}

export interface VisitPayload {
  roundId: string;
  kind: RoundVisitKind;
  targetId: string;
  label: string;
  /** Stamped when the visit happened, so a late delivery can't jump the list. */
  visitedAt: string;
}

export function createChecklistMarkSync(args: {
  userId: string;
  onSuccess: (
    result: ChecklistMarkFlushResult,
    payload: ChecklistMarkPayload,
  ) => void;
  shouldCommit?: () => boolean;
  storage?: StorageLike;
  isOnline?: () => boolean;
}): SyncFeature<ChecklistMarkPayload> {
  return createSyncFeature<ChecklistMarkPayload, ChecklistMarkFlushResult>({
    storageKey: `todoRailSync.marks.v1.${args.userId}`,
    // One queued write per item: a later verdict on the same step replaces an
    // earlier one, exactly as the server's last-write-wins would have. Ticking
    // and un-ticking while offline must drain as the final answer, not as a
    // sequence the funder would watch replay.
    coalesce: (existing, incoming) =>
      existing.roundId === incoming.roundId &&
      existing.itemKey === incoming.itemKey
        ? incoming
        : null,
    send: (payload) => trpcVanilla.setRoundChecklistMark.mutate(payload),
    onSuccess: args.onSuccess,
    storage: args.storage,
    isOnline: args.isOnline,
    shouldCommit: args.shouldCommit,
  });
}

export interface VideoCompletionPayload {
  roundId: string;
  videoId: string;
  completed: boolean;
  clientUpdatedAt: string;
}

export interface VideoCompletionFlushResult {
  applied: boolean;
  current: ProgressRow;
}

/**
 * "Mark as complete" / "Mark as not done" on a video.
 *
 * On the same durable path as the checklist marks, and for the same reason:
 * it is a deliberate statement by the funder, so failing it silently offline —
 * showing the tick, dropping the write, then reverting on the next refetch —
 * loses something no amount of replaying can reconstruct. This was the one rail
 * write left on a raw mutation after the others moved.
 */
export function createVideoCompletionSync(args: {
  userId: string;
  onSuccess: (
    result: VideoCompletionFlushResult,
    payload: VideoCompletionPayload,
  ) => void;
  shouldCommit?: () => boolean;
  storage?: StorageLike;
  isOnline?: () => boolean;
}): SyncFeature<VideoCompletionPayload> {
  return createSyncFeature<VideoCompletionPayload, VideoCompletionFlushResult>({
    storageKey: `todoRailSync.videoCompletion.v1.${args.userId}`,
    // One verdict per video: flip-flopping offline drains as the final answer.
    coalesce: (existing, incoming) =>
      existing.roundId === incoming.roundId &&
      existing.videoId === incoming.videoId
        ? incoming
        : null,
    send: (payload) => trpcVanilla.setVideoCompleted.mutate(payload),
    onSuccess: args.onSuccess,
    storage: args.storage,
    isOnline: args.isOnline,
    shouldCommit: args.shouldCommit,
  });
}

export function createVisitSync(args: {
  userId: string;
  onSuccess: (result: { applied: boolean }, payload: VisitPayload) => void;
  shouldCommit?: () => boolean;
  storage?: StorageLike;
  isOnline?: () => boolean;
}): SyncFeature<VisitPayload> {
  return createSyncFeature<VisitPayload, { applied: boolean }>({
    storageKey: `todoRailSync.visits.v1.${args.userId}`,
    // A visit is an upsert of "when was I last here", so re-visiting the same
    // target while offline is one row, not a backlog of identical ones.
    coalesce: (existing, incoming) =>
      existing.roundId === incoming.roundId &&
      existing.kind === incoming.kind &&
      existing.targetId === incoming.targetId
        ? incoming
        : null,
    send: (payload) => trpcVanilla.recordRoundVisit.mutate(payload),
    onSuccess: args.onSuccess,
    storage: args.storage,
    isOnline: args.isOnline,
    shouldCommit: args.shouldCommit,
  });
}
